/*
 * Dated delivery line.
 *
 * Works out "printed <day>, delivered by <date>" in the store's own time zone, in the
 * browser, so a cached page can never show a stale date. Settings and copy come from
 * window.themeDeliveryEstimate, set by snippets/delivery-estimate-assets.liquid.
 */
(function () {
  'use strict';

  var DAY = 86400000;
  var DRAWER_ID = 'side-cart';
  var CHECKOUT_BUTTON = '.side-cart__checkout-button';
  var TEMPLATE_ID = 'delivery-estimate-cart-template';

  var config = window.themeDeliveryEstimate;
  if (!config || typeof Intl === 'undefined' || !Intl.DateTimeFormat) return;

  var holidays = parseHolidays(config.holidays);
  var pendingMount = false;

  /* Every setting below is merchant-editable, so none of it is trusted as given. */
  var cutoffHour = wholeNumber(config.cutoffHour, 10, 0, 23);
  var printDays = wholeNumber(config.printDays, 1, 0, 60);
  var deliveryDays = wholeNumber(config.deliveryDays, 13, 0, 120);
  var calendarDays = config.dayCount !== 'working';
  var storeClock = createStoreClock(config.timezone);

  function wholeNumber(value, fallback, min, max) {
    var parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < min || parsed > max) return fallback;
    return parsed;
  }

  /*
   * The time zone is typed by hand in the theme editor. An unknown IANA name throws a
   * RangeError, so build the formatter once here and fall back to the shopper's own zone
   * rather than letting the line die. The warning is how a typo gets noticed.
   */
  function createStoreClock(timezone) {
    var options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };

    try {
      options.timeZone = timezone;
      return new Intl.DateTimeFormat('en-GB', options);
    } catch (error) {
      if (window.console && console.warn) {
        console.warn(
          'Delivery estimate: "' + timezone + '" is not a time zone this browser knows. ' +
            'Falling back to the visitor\'s own zone. Set a valid IANA name, such as Europe/Amsterdam, ' +
            'in Theme settings under Delivery estimate.'
        );
      }
      delete options.timeZone;
      return new Intl.DateTimeFormat('en-GB', options);
    }
  }

  function parseHolidays(raw) {
    var dates = {};
    if (!raw) return dates;
    String(raw).split(/[\s,;]+/).forEach(function (entry) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(entry)) dates[entry] = true;
    });
    return dates;
  }

  function isoDay(stamp) {
    return new Date(stamp).toISOString().slice(0, 10);
  }

  function isWorkingDay(stamp) {
    var weekday = new Date(stamp).getUTCDay();
    if (weekday === 0) return false;
    if (weekday === 6 && !config.saturdayIsWorking) return false;
    return !holidays[isoDay(stamp)];
  }

  function nextWorkingDay(stamp) {
    var candidate = stamp;
    for (var step = 0; step < 400; step++) {
      candidate += DAY;
      if (isWorkingDay(candidate)) return candidate;
    }
    return candidate;
  }

  function addWorkingDays(stamp, count) {
    var result = stamp;
    for (var added = 0; added < count; added++) result = nextWorkingDay(result);
    return result;
  }

  /*
   * Calendar mode counts every day, then moves a date that lands on a Sunday or a
   * non-working date on to the next day that is neither, since nobody delivers then.
   * Saturday stays a delivery day whatever the working-day setting says.
   */
  function addCalendarDays(stamp, count) {
    var result = stamp + count * DAY;
    for (var step = 0; step < 400; step++) {
      if (new Date(result).getUTCDay() !== 0 && !holidays[isoDay(result)]) break;
      result += DAY;
    }
    return result;
  }

  /* The store's current date as a midnight-anchored UTC stamp, plus its clock hour. */
  function storeNow() {
    var parts = {};
    storeClock
      .formatToParts(new Date())
      .forEach(function (part) {
        parts[part.type] = part.value;
      });

    var hour = parseInt(parts.hour, 10);
    if (hour === 24) hour = 0;

    return {
      day: Date.UTC(parseInt(parts.year, 10), parseInt(parts.month, 10) - 1, parseInt(parts.day, 10)),
      hour: hour
    };
  }

  /* An unknown or malformed locale tag throws, so fall back to the browser's own. */
  function formatUTC(stamp, locale, options) {
    options.timeZone = 'UTC';
    try {
      return new Intl.DateTimeFormat(locale, options).format(new Date(stamp));
    } catch (error) {
      return new Intl.DateTimeFormat(undefined, options).format(new Date(stamp));
    }
  }

  function dateLabel(stamp, locale, month) {
    return formatUTC(stamp, locale, { day: 'numeric', month: month || 'short' });
  }

  function dayLabel(stamp, today, locale) {
    var offset = Math.round((stamp - today) / DAY);
    if (offset === 0) return config.strings.today;
    if (offset === 1) return config.strings.tomorrow;
    if (offset < 7) {
      return formatUTC(stamp, locale, { weekday: 'long' });
    }
    return dateLabel(stamp, locale);
  }

  /*
   * The cart has its own shorter wording, one promise with no cut-off or print day:
   * "Vandaag besteld, verwacht op 8 oktober". The date is worked out the same way,
   * so an order after the cut-off still gets the later date. Without that string the
   * cart falls back to the product-page wording.
   *
   * month is 'long' or 'short'. The cart asks for 'long' first; see fitCartLine.
   */
  function buildLine(placement, month) {
    var now = storeNow();
    if (isNaN(now.day)) return '';

    var beforeCutoff = isWorkingDay(now.day) && now.hour < cutoffHour;
    var start = beforeCutoff ? now.day : nextWorkingDay(now.day);
    var printed = addWorkingDays(start, printDays);
    var delivered = calendarDays ? addCalendarDays(start, deliveryDays) : addWorkingDays(start, deliveryDays);
    if (delivered < printed) delivered = printed;

    var template = beforeCutoff ? config.strings.beforeCutoff : config.strings.afterCutoff;
    if (placement === 'cart' && config.strings.cart) template = config.strings.cart;
    if (!template) return '';

    var locale = config.locale || document.documentElement.lang || 'en';
    var cutoff = (cutoffHour < 10 ? '0' : '') + cutoffHour + ':00';

    return template
      .replace('[cutoff]', cutoff)
      .replace('[print_day]', dayLabel(printed, now.day, locale))
      .replace('[delivery_date]', dateLabel(delivered, locale, month));
  }

  function renderNode(node, line) {
    if (!line) {
      node.hidden = true;
      return;
    }
    var text = node.querySelector('.delivery-estimate__text');
    /*
     * Only write when the line actually changed. Assigning textContent replaces the
     * child text node even when the string is identical, and inside the cart drawer
     * that is a mutation the drawer observer would see and answer with another render.
     */
    if (text && text.textContent !== line) text.textContent = line;
    if (node.hidden) node.hidden = false;
  }

  /* True when the line's text runs onto a second line. */
  function wraps(node) {
    var text = node.querySelector('.delivery-estimate__text');
    if (!text || !text.firstChild || !document.createRange) return false;

    var range = document.createRange();
    range.selectNodeContents(text);
    var rects = range.getClientRects();
    if (!rects.length) return false;

    var firstTop = rects[0].top;
    for (var i = 1; i < rects.length; i++) {
      if (rects[i].top - firstTop > 1) return true;
    }
    return false;
  }

  /*
   * The cart spells the month out ("8 oktober") when that fits on one line, and falls
   * back to the short month ("8 okt") when it would wrap. The fit is measured on the
   * rendered line, not guessed from the viewport, since the drawer and the cart page
   * give it different widths. A line with no width yet, such as one inside a closed
   * drawer, keeps the long form; the resize observer measures it again once it shows.
   */
  function fitCartLine(node) {
    renderNode(node, buildLine('cart', 'long'));
    if (node.hidden || !node.offsetWidth) return;
    if (wraps(node)) renderNode(node, buildLine('cart', 'short'));
  }

  /*
   * A cart line is measured again whenever its own box changes size: the window is
   * resized, the drawer opens, or the drawer re-renders around it.
   *
   * The fit waits for the next frame. Changing the line's height inside the observer's
   * own callback makes the browser report "ResizeObserver loop completed with
   * undelivered notifications" as a page error. The short month's new height reports
   * once more and settles on the same result, so this does not loop.
   */
  var pendingFits = [];

  function fitPending() {
    var nodes = pendingFits;
    pendingFits = [];
    nodes.forEach(function (node) {
      if (node.isConnected) fitCartLine(node);
    });
  }

  var resizeObserver =
    window.ResizeObserver &&
    new ResizeObserver(function (entries) {
      if (!pendingFits.length) window.requestAnimationFrame(fitPending);
      entries.forEach(function (entry) {
        if (pendingFits.indexOf(entry.target) === -1) pendingFits.push(entry.target);
      });
    });

  function renderElement(node) {
    if (node.dataset.placement !== 'cart') {
      renderNode(node, buildLine(node.dataset.placement));
      return;
    }
    fitCartLine(node);
    if (resizeObserver) resizeObserver.observe(node);
  }

  function render(root) {
    var nodes = (root || document).querySelectorAll('delivery-estimate');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, renderElement);
  }

  /*
   * Registering the element means it fills itself in whenever it enters the page,
   * however it got there: rendered by Liquid, cloned into the cart drawer, or pulled
   * into the quick-buy popup, which fetches the product page and keeps only the
   * markup. There is no insertion hook to subscribe to, and no script comes across
   * with the markup, so the element has to be able to do this on its own.
   */
  function defineElement() {
    if (!window.customElements || customElements.get('delivery-estimate')) return false;

    try {
      customElements.define(
        'delivery-estimate',
        class extends HTMLElement {
          connectedCallback() {
            renderElement(this);
          }

          disconnectedCallback() {
            if (resizeObserver) resizeObserver.unobserve(this);
          }
        }
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  /*
   * The line is cloned in after the drawer's checkout button, which puts it inside
   * the drawer's own footer. The side cart re-renders its inner HTML on every cart
   * change, so the clone goes with it; the observer below mounts it again.
   */
  function mountInDrawer() {
    if (!config.cartEnabled) return;

    var button = document.querySelector(CHECKOUT_BUTTON);
    if (!button || !button.parentNode) return;

    var host = button.parentNode;
    var existing = host.querySelector('delivery-estimate');

    /*
     * The side cart marks its checkout button "false" when every paid line is a
     * product with its own delivery promise, such as the colouring book. That page
     * says "morgen in huis", so a later cart date would contradict it.
     */
    if (button.getAttribute('data-delivery-estimate') === 'false') {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }

    if (!existing) {
      var template = document.getElementById(TEMPLATE_ID);
      if (!template || !template.content || !template.content.firstElementChild) return;
      button.insertAdjacentElement('afterend', template.content.firstElementChild.cloneNode(true));
    }
    render(host);
  }

  function scheduleMount() {
    if (pendingMount) return;
    pendingMount = true;
    setTimeout(function () {
      pendingMount = false;
      mountInDrawer();
    }, 100);
  }

  function watchDrawer() {
    if (!config.cartEnabled || !window.MutationObserver) return;

    var drawer = document.getElementById(DRAWER_ID);

    if (!drawer) {
      setTimeout(watchDrawer, 500);
      return;
    }

    new MutationObserver(onDrawerMutations).observe(drawer, { childList: true, subtree: true });
    scheduleMount();
  }

  /* Our own writes are inside the drawer, so ignore them or the observer answers itself. */
  function onDrawerMutations(mutations) {
    var external = mutations.some(function (mutation) {
      var target = mutation.target;
      if (target && target.nodeType !== 1) target = target.parentNode;
      return !(target && target.closest && target.closest('delivery-estimate'));
    });
    if (external) scheduleMount();
  }

  function start() {
    /* Defining the element upgrades any node already on the page, which renders it. */
    if (!defineElement()) render();
    watchDrawer();
  }

  [
    'side-cart:updated',
    'side-cart:item-added',
    'side-cart:item-removed',
    'side-cart:quantity-changed',
    'theme:cartchanged'
  ].forEach(function (name) {
    window.addEventListener(name, scheduleMount);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
